import { useEffect, useState } from 'react';
import useInView from '../hooks/useInView';

/* Smooth counter with cubic ease-out */
function useCounter(target, started, duration = 1800) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!started) return;
    let frame = 0;
    const totalFrames = Math.round(duration / 16);
    const timer = setInterval(() => {
      frame++;
      const p = frame / totalFrames;
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.min(Math.round(eased * target), target));
      if (frame >= totalFrames) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [started, target, duration]);
  return val;
}

const STATS = [
  { target: 120, suffix: '+', label: 'Offers Made', sub: 'Across all verticals' },
  { target: 15, suffix: '+', label: 'Client Partners', sub: 'Active relationships' },
  { target: 30, suffix: '+', label: 'Yrs Combined Experience', sub: 'Leadership team' },
];

/* Logos extracted directly from the IndUS Target pitch deck */
const CLIENTS = [
  { name: 'Infosys',         logo: '/logos/logo_07_302x133.png' },
  { name: 'ITC Infotech',    logo: '/logos/logo_08_249x131.jpeg' },
  { name: 'USDM',            logo: '/logos/logo_09_330x138.jpeg' },
  { name: 'MaxPoint',        logo: '/logos/logo_10_395x119.jpeg' },
  { name: 'Monsanto',        logo: '/logos/logo_11_364x140.png' },
  { name: 'Pocket FM',       logo: '/logos/logo_12_310x131.png' },
  { name: 'HighLevel',       logo: '/logos/logo_13_418x132.png' },
  { name: 'Quince',          logo: '/logos/logo_14_351x79.png' },
  { name: 'glance',          logo: '/logos/logo_15_379x54.png' },
  { name: 'MerakiLabs',      logo: '/logos/logo_16_445x122.png' },
  { name: 'Breakout',        logo: '/logos/logo_18_398x121.jpeg' },
];

function StatCard({ target, suffix, label, sub, started, delay }) {
  const val = useCounter(target, started);
  return (
    <div
      className="text-center px-6 py-10"
      style={{
        opacity: started ? 1 : 0,
        transform: started ? 'translateY(0)' : 'translateY(20px)',
        transition: `all 0.65s ease ${delay}ms`,
      }}
      data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div
        className="text-5xl lg:text-6xl font-black text-[#0A1628] tabular-nums leading-none mb-2"
        style={{ fontFamily: '"Satoshi","Inter",sans-serif' }}
      >
        {started ? val : 0}{suffix}
      </div>
      <div
        className="text-xs font-semibold text-[#0A1628] uppercase tracking-[0.18em] mb-1"
        style={{ fontFamily: '"Inter",sans-serif' }}
      >
        {label}
      </div>
      <div className="text-xs text-[#9CA3AF]" style={{ fontFamily: '"Inter",sans-serif' }}>{sub}</div>
    </div>
  );
}

function ClientLogo({ name, logo }) {
  const [imgError, setImgError] = useState(false);
  return (
    <div
      className="flex-shrink-0 flex flex-col items-center justify-center gap-2 px-8 py-4 opacity-50 hover:opacity-100 hover:scale-105 transition-all duration-300 cursor-default group"
      data-testid={`client-logo-${name.toLowerCase().replace(/\s/g, '-')}`}
    >
      {!imgError ? (
        <img
          src={logo}
          alt={name}
          className="h-8 max-w-[130px] object-contain grayscale group-hover:grayscale-0 transition-all duration-300"
          onError={() => setImgError(true)}
        />
      ) : (
        <span
          className="font-bold text-sm text-[#0A1628] tracking-wider whitespace-nowrap"
          style={{ fontFamily: '"Inter",sans-serif' }}
        >
          {name}
        </span>
      )}
    </div>
  );
}

export default function CredibilitySection() {
  const [ref, inView] = useInView(0.2);

  return (
    <section id="credibility" className="bg-white border-b border-black/5" data-testid="credibility-section">
      {/* Stats */}
      <div ref={ref} className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-black/6" data-testid="stats-row">
          {STATS.map((s, i) => (
            <StatCard key={i} {...s} started={inView} delay={i * 160} />
          ))}
        </div>
      </div>

      {/* Client logos — from pitch deck */}
      <div className="border-t border-black/5 py-12">
        <p
          className="text-center text-xs uppercase tracking-[0.25em] font-semibold text-[#9CA3AF] mb-8"
          style={{ fontFamily: '"Inter",sans-serif' }}
        >
          Trusted by industry leaders
        </p>
        <div className="logos-marquee-wrapper overflow-hidden">
          <div className="logos-marquee-track flex">
            {[...CLIENTS, ...CLIENTS, ...CLIENTS].map((c, i) => (
              <ClientLogo key={i} {...c} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
